<template>
    <v-dialog v-model="show" persistent width="500px">
        <v-wait for="loading date-based task data">
            <template slot="waiting">
                <v-card>
                    <v-toolbar dark color="primary"></v-toolbar>
                    <vcl-list height="200"></vcl-list>
                </v-card>
            </template>
            <v-card v-if="dateBasedTask">
                <v-toolbar dark color="primary">
                    <v-toolbar-title>
                        {{ dateBasedTask.description }} for
                        <router-link
                            class="accent--text text--lighten-1"
                            :to="{ name: 'unit', params: { unitId: dateBasedTask.unitId } }"
                        >{{ dateBasedTask.unit }}</router-link>
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn @click="close" icon text>
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-layout column class="mx-4">
                    <v-checkbox
                        v-if="!isFutureDateBasedTask"
                        v-model="model.completedOnScheduledDate"
                        class="completed-date-input"
                    >
                        <template
                            v-slot:label
                            v-if="model.completedOnScheduledDate"
                        >Scheduled date ({{ dateBasedTask.dueDate | formatDate }})</template>
                        <template v-slot:prepend>Completed&nbsp;on</template>
                        <template v-slot:append v-if="!model.completedOnScheduledDate">
                            <date
                                v-model="model.completedDate"
                                v-validate="'required'"
                                required
                                name="completed date"
                                :error-messages="errors.first('completed date')"
                                :max="today"
                            ></date>
                        </template>
                    </v-checkbox>
                    <date
                        v-else
                        v-model="model.completedDate"
                        v-validate="'required'"
                        required
                        name="completed date"
                        :error-messages="errors.first('completed date')"
                        :max="today"
                    >
                        <template v-slot:prepend>Completed&nbsp;on</template>
                    </date>
                </v-layout>

                <v-layout column class="mx-4">
                    <decimal
                        v-model="model.completedAtHours"
                        placeholder="what hours was this completed at?"
                        v-validate="'hr-in-range'"
                        name="completed at hours"
                        :error-messages="errors.first('completed at hours')"
                        :precision="1"
                    >
                        <template v-slot:prepend>Completed&nbsp;at</template>
                        <template v-slot:append>hrs</template>
                    </decimal>
                </v-layout>

                <v-layout column class="mx-4">
                    <decimal
                        v-model="model.completedAtKilometres"
                        placeholder="what kilometres was this completed at?"
                        v-validate="'km-in-range'"
                        name="completed at kilometres"
                        :error-messages="errors.first('completed at kilometres')"
                        :precision="1"
                    >
                        <template v-slot:prepend>Completed&nbsp;at</template>
                        <template v-slot:append>kms</template>
                    </decimal>
                </v-layout>

                <v-layout column class="mx-4">
                    <v-text-field
                        v-model="model.completedBy"
                        placeholder="by who? (e.g. 060 - Kris Shepherd)"
                        v-validate="'required'"
                        name="completed by"
                        maxlength="50"
                        :error-messages="errors.first('completed by')"
                        required
                    >
                        <template v-slot:prepend>Completed&nbsp;by</template>
                    </v-text-field>
                </v-layout>
                <v-card-actions v-if="dateBasedTask">
                    <v-spacer></v-spacer>
                    <v-btn @click="close">Cancel</v-btn>
                    <v-btn
                        color="primary"
                        :disabled="disableSubmit"
                        @click="submit"
                    >{{ dateBasedTask.completedDate ? 'Update' : 'Complete' }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-wait>
    </v-dialog>
</template>
<script>
import { Event } from '@/plugins/bus';
import DateBasedTasksClient from '@/components/DateBasedTasks/DateBasedTasksClient.vue';
import { VclList } from 'vue-content-loading';
import moment from 'moment';
import Decimal from '@/components/Decimal.vue';
import Date from '@/components/Date.vue';
import { DateFormat } from '@/plugins/date-filter';
import { Validator } from 'vee-validate';
import { IntervalType } from '@/models/enums';
import MetricToleranceCheck from '@/components/MetricToleranceCheck';

export default {
    mixins: [DateBasedTasksClient, MetricToleranceCheck],
    components: {
        VclList,
        Decimal,
        Date
    },
    data() {
        return {
            dateBasedTask: null,
            show: null,
            model: {},
            today: moment().format(DateFormat.Canonical)
        };
    },
    computed: {
        dateBasedTaskId() {
            return this.$route.query && this.$route.query.dateBasedTaskId;
        },
        completedDate() {
            return this.model.completedOnScheduledDate ? this.dateBasedTask.dueDate : this.model.completedDate;
        },
        disableSubmit() {
            return Object.keys(this.fields).some(key => this.fields[key].validated && this.fields[key].invalid);
        },
        isFutureDateBasedTask() {
            return moment(this.dateBasedTask.dueDate).isAfter(moment());
        }
    },
    methods: {
        submit(event) {
            this.$validator.validate().then(this.complete);
        },
        complete(valid) {
            if (!valid) {
                return;
            }
            this.completeDateBasedTask(this.model);
        },
        close() {
            this.dateBasedTask = null;
            this.$router.back();
        },
        resetModel() {
            this.model = {
                id: this.dateBasedTask.id,
                completedDate: this.isFutureDateBasedTask ? moment().format(DateFormat.Canonical) : this.dateBasedTask.dueDate,
                completedBy: null,
                completedAtHours: null,
                completedAtKilometres: null,
                completedOnScheduledDate: !this.isFutureDateBasedTask
            };

            if (this.dateBasedTask.completedDate) {
                this.model.completedDate = this.dateBasedTask.completedDate;
                this.model.completedBy = this.dateBasedTask.completedBy;
                this.model.completedAtHours = this.dateBasedTask.completedAtHours;
                this.model.completedAtKilometres = this.dateBasedTask.completedAtKilometres;
            }
        },
        handleDateBasedTaskUpdated(dateBasedTask) {
            this.dateBasedTask = dateBasedTask;
            this.resetModel();
        }
    },
    watch: {
        show(val) {
            if (val) {
                this.getDateBasedTask(this.dateBasedTaskId);
            }
        },
        $route: {
            handler(val) {
                this.show = !!this.dateBasedTaskId;
            },
            deep: true
        }
    },
    mounted() {
        this.show = !!this.dateBasedTaskId;

        this.$bus.$on(Event.DateBasedTaskUpdated, this.handleDateBasedTaskUpdated);
        this.$bus.$on(Event.DateBasedTaskCompleted, this.close);

        const isHrInRange = async value => await this.checkMetricIsInRange(this.dateBasedTask.unitId, IntervalType.Hours, value, this.model.completedDate);
        const isKmInRange = async value => await this.checkMetricIsInRange(this.dateBasedTask.unitId, IntervalType.Kilometres, value, this.model.completedDate);

        Validator.extend('hr-in-range', {
            validate: isHrInRange,
            getMessage: (field, params, data) => data.message
        });

        Validator.extend('km-in-range', {
            validate: isKmInRange,
            getMessage: (field, params, data) => data.message
        });
    },
    destroyed() {
        this.$bus.$off(Event.DateBasedTaskUpdated, this.handleDateBasedTaskUpdated);
        this.$bus.$off(Event.DateBasedTaskCompleted, this.close);
    }
};
</script>
<style>
.completed-date-input {
    padding-top: 10px;
    margin-top: 10px;
}
</style>
